/**
 * Renders the "Previous Added Notes" list shown on the Status Logs Notes tab.
 * @module PreviousAddedNotes
 */

import { StatusLogDataWithId } from "../../../../../data-model/statusLogModel";
import { formatRelativeNoteTime } from "../../utils/statusLogHelpers";

/** Props for {@link PreviousAddedNotes}. */
export interface PreviousAddedNotesProps {
  /** Added-notes entries to display, newest first preferred. */
  notes: StatusLogDataWithId[];
  /** True while comments are still loading (or have not started yet). */
  isLoading: boolean;
  /** True when the comments fetch failed. */
  hasError?: boolean;
}

/**
 * Lists prior "Added notes" events with author, relative time, and note body.
 *
 * @param props - Notes list, loading, and error state
 */
export const PreviousAddedNotes = (props: PreviousAddedNotesProps) => {
  const { notes, isLoading, hasError = false } = props;

  return (
    <div className="flex flex-col gap-4">
      <div className="text-[0.875rem] font-semibold leading-[1.375rem] text-primaryGray-1">
        Previous Added Notes
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <span
            aria-hidden
            className="inline-block h-5 w-5 animate-spin rounded-full border-[3px] border-solid border-[#0F0F0F] border-r-transparent"
          />
        </div>
      ) : hasError ? (
        <div className="rounded bg-primaryGray-16 px-3 py-4 text-center text-tiny text-primaryGray-6">
          Unable to load notes. Please try again.
        </div>
      ) : notes.length === 0 ? (
        <div className="rounded bg-primaryGray-16 px-3 py-4 text-center text-tiny text-primaryGray-6">
          No notes added yet
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {notes.map((entry) => (
            <div
              key={entry.id}
              className="flex w-full flex-col items-start gap-1 self-stretch rounded-[0.375rem] border border-[#E5E7EB] bg-[#F9FAFB] p-4"
            >
              <div className="flex w-full flex-row items-center justify-between gap-2">
                <span className="text-[0.8125rem] font-semibold leading-[1.25rem] text-[#0F0F0F]">
                  {entry.provider_name || "Unknown"}
                </span>
                <span className="shrink-0 text-tiny font-normal text-primaryGray-6">
                  {formatRelativeNoteTime(entry.created_at ?? "")}
                </span>
              </div>
              <div className="w-full text-wrap text-[0.8125rem] font-normal leading-4 text-[#374151]">
                {entry.comments}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
